import { useQuery } from '@tanstack/react-query';
import { holidays as holidaysApi, type Holiday } from './api';
import { useAuthStore } from '@/stores/auth';

/**
 * React Query hook to fetch holidays for a given year.
 */
export function useHolidays(year?: number) {
  const { token } = useAuthStore();
  const targetYear = year || new Date().getFullYear();

  return useQuery({
    queryKey: ['holidays', targetYear],
    queryFn: () => holidaysApi.list(token!, { year: targetYear }),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data.data,
  });
}

/**
 * Check if a given date string (YYYY-MM-DD) is a holiday.
 */
export function isHoliday(dateStr: string, holidayList: Holiday[]): boolean {
  return holidayList.some((h) => h.date === dateStr);
}

/**
 * Get the holiday for a given date, if any.
 */
export function getHoliday(dateStr: string, holidayList: Holiday[]): Holiday | undefined {
  return holidayList.find((h) => h.date === dateStr);
}

/**
 * Get the holiday name for a given date, if any.
 */
export function getHolidayName(dateStr: string, holidayList: Holiday[]): string | undefined {
  return holidayList.find((h) => h.date === dateStr)?.name;
}

/**
 * Get the holiday type for a given date, if any.
 */
export function getHolidayType(dateStr: string, holidayList: Holiday[]): Holiday['type'] | undefined {
  return holidayList.find((h) => h.date === dateStr)?.type;
}

/**
 * Format a date as YYYY-MM-DD (matching the holiday date format).
 */
export function formatDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
